import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
    Grid, Slider, Stack, Tab, Tabs, TextField, Typography, useTheme
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { tokens } from "../theme/theme";
import { UserContext } from "../theme/userContext";

const CATEGORY_LABELS = {
    valuation: 'Bewertung',
    growth: 'Wachstum',
    profitability: 'Profitabilität',
    balance_sheet: 'Bilanz',
    earnings: 'Earnings',
    analyst: 'Analysten/Insider',
    news: 'News',
    technical: 'Technicals',
    dividend: 'Dividende',
};

const STRATEGY_TAB_LABELS = [
    { key: 'global', label: 'Global (Fallback)' },
    { key: 'growth', label: 'Wachstum' },
    { key: 'dividend', label: 'Dividende' },
    { key: 'longterm', label: 'Langfristig' },
];

const OVERLAY_LABELS = {
    positionWeight: { title: 'Positionsgröße', unit: '%', field: 'maxPct' },
    sectorOverweight: { title: 'Sektor-Übergewicht', unit: '% über Ideal', field: 'maxOverPct' },
    currencyOverweight: { title: 'Währungs-Übergewicht', unit: '% über Ideal', field: 'maxOverPct' },
};

const SIGNAL_COLORS = { GREEN: '#4caf50', YELLOW: '#ff9800', RED: '#f44336' };

const AdminTabTrafficLight = () => {
    const { user } = useContext(UserContext);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [simulating, setSimulating] = useState(false);
    const [simulation, setSimulation] = useState(null);
    const [message, setMessage] = useState(null);
    const [strategyTab, setStrategyTab] = useState(0); // 0=global, 1=growth, 2=dividend, 3=longterm

    // Load config from API
    useEffect(() => {
        const base = process.env.REACT_APP_API_URI;
        fetch(`${base}/api/trafficlight/config`)
            .then(r => r.json())
            .then(data => {
                setConfig(data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, []);

    if (loading || !config) return <Box p={2}><CircularProgress size={24} /> Lade Konfiguration...</Box>;

    const strategyProfiles = config.strategy_profiles || {};
    const currentStrategyKey = STRATEGY_TAB_LABELS[strategyTab]?.key || 'global';
    const isGlobalTab = currentStrategyKey === 'global';

    // Resolve current weights + thresholds based on active tab
    const weights = isGlobalTab
        ? (config.category_weights || {})
        : (strategyProfiles[currentStrategyKey]?.category_weights || config.category_weights || {});
    const thresholds = isGlobalTab
        ? (config.signal_thresholds || { green: 65, yellow: 45 })
        : (strategyProfiles[currentStrategyKey]?.signal_thresholds || config.signal_thresholds || { green: 65, yellow: 45 });
    const overlayRules = config.overlay_rules || {};
    const maxPenalty = config.overlay_max_penalty != null ? config.overlay_max_penalty : -15;
    const weightSum = Object.values(weights).reduce((a, b) => a + b, 0);
    const weightSumOk = Math.abs(weightSum - 100) < 0.01;

    // Weight change handler
    const handleWeightChange = (category, newValue) => {
        if (isGlobalTab) {
            setConfig(prev => ({
                ...prev,
                category_weights: { ...prev.category_weights, [category]: newValue },
            }));
        } else {
            setConfig(prev => {
                const profiles = { ...prev.strategy_profiles };
                const profile = { ...(profiles[currentStrategyKey] || {}) };
                profile.category_weights = { ...(profile.category_weights || prev.category_weights), [category]: newValue };
                profiles[currentStrategyKey] = profile;
                return { ...prev, strategy_profiles: profiles };
            });
        }
        setSimulation(null);
    };

    // Threshold change handler
    const handleThresholdChange = (key, value) => {
        if (isGlobalTab) {
            setConfig(prev => ({
                ...prev,
                signal_thresholds: { ...prev.signal_thresholds, [key]: Number(value) },
            }));
        } else {
            setConfig(prev => {
                const profiles = { ...prev.strategy_profiles };
                const profile = { ...(profiles[currentStrategyKey] || {}) };
                profile.signal_thresholds = { ...(profile.signal_thresholds || prev.signal_thresholds), [key]: Number(value) };
                profiles[currentStrategyKey] = profile;
                return { ...prev, strategy_profiles: profiles };
            });
        }
        setSimulation(null);
    };

    // Overlay step change handler
    const handleOverlayStepChange = (ruleKey, stepIdx, field, value) => {
        setConfig(prev => {
            const rules = { ...prev.overlay_rules };
            const steps = [...(rules[ruleKey] || [])];
            steps[stepIdx] = { ...steps[stepIdx], [field]: Number(value) };
            rules[ruleKey] = steps;
            return { ...prev, overlay_rules: rules };
        });
        setSimulation(null);
    };

    // Max penalty change
    const handleMaxPenaltyChange = (value) => {
        setConfig(prev => ({ ...prev, overlay_max_penalty: Number(value) }));
        setSimulation(null);
    };

    // Save config
    const handleSave = async () => {
        if (!weightSumOk) {
            setMessage({ type: 'error', text: `Gewichte müssen 100 ergeben (aktuell: ${weightSum})` });
            return;
        }
        setSaving(true);
        setMessage(null);
        try {
            const base = process.env.REACT_APP_API_URI;
            const response = await fetch(`${base}/api/trafficlight/config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config),
            });
            const data = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: 'Konfiguration gespeichert!' });
                if (data.config) setConfig(data.config);
            } else {
                setMessage({ type: 'error', text: data.message || 'Fehler beim Speichern' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
        setSaving(false);
    };

    // Simulate
    const handleSimulate = async () => {
        setSimulating(true);
        setMessage(null);
        try {
            const base = process.env.REACT_APP_API_URI;
            const response = await fetch(`${base}/api/trafficlight/simulate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ puser: user.p_user, config }),
            });
            const data = await response.json();
            if (response.ok) {
                setSimulation(data);
            } else {
                setMessage({ type: 'error', text: data.message || 'Simulation fehlgeschlagen' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
        setSimulating(false);
    };

    return (
        <Box p={2}>
            {message && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* ── Kategorie-Gewichte ── */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Kategorie-Gewichte
                    </Typography>
                    <Tabs
                        value={strategyTab}
                        onChange={(e, v) => setStrategyTab(v)}
                        textColor="secondary"
                        indicatorColor="secondary"
                        sx={{ mb: 2 }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {STRATEGY_TAB_LABELS.map((t) => (
                            <Tab key={t.key} label={t.label} sx={{ textTransform: 'none', minWidth: 80 }} />
                        ))}
                    </Tabs>
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                        Summe: <span style={{ color: weightSumOk ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                            {weightSum}
                        </span> / 100
                    </Typography>

                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                        <Box key={key} sx={{ mb: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2">{label}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 30, textAlign: 'right' }}>
                                    {weights[key] || 0}
                                </Typography>
                            </Stack>
                            <Slider
                                value={weights[key] || 0}
                                onChange={(e, v) => handleWeightChange(key, v)}
                                min={0} max={40} step={1}
                                size="small"
                                valueLabelDisplay="auto"
                                sx={{ py: 0.5 }}
                            />
                        </Box>
                    ))}
                </Grid>

                {/* ── Schwellen + Max Penalty ── */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Signal-Schwellen
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
                        Score &ge; Grün → GREEN, Score &ge; Gelb → YELLOW, sonst RED
                    </Typography>

                    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                        <TextField
                            label="Grün-Schwelle"
                            type="number"
                            value={thresholds.green}
                            onChange={(e) => handleThresholdChange('green', e.target.value)}
                            size="small"
                            inputProps={{ min: 0, max: 100 }}
                            sx={{ width: 130 }}
                        />
                        <TextField
                            label="Gelb-Schwelle"
                            type="number"
                            value={thresholds.yellow}
                            onChange={(e) => handleThresholdChange('yellow', e.target.value)}
                            size="small"
                            inputProps={{ min: 0, max: 100 }}
                            sx={{ width: 130 }}
                        />
                        <TextField
                            label="Max. Overlay"
                            type="number"
                            value={maxPenalty}
                            onChange={(e) => handleMaxPenaltyChange(e.target.value)}
                            size="small"
                            inputProps={{ min: -50, max: 0 }}
                            sx={{ width: 130 }}
                        />
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* ── Overlay-Regeln ── */}
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Overlay-Stufen
                    </Typography>

                    {Object.entries(OVERLAY_LABELS).map(([ruleKey, { title, unit, field }]) => (
                        <Box key={ruleKey} sx={{ mb: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                {title}
                            </Typography>
                            <Stack spacing={0.5}>
                                {(overlayRules[ruleKey] || []).map((step, idx) => (
                                    <Stack key={idx} direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" sx={{ minWidth: 20, opacity: 0.6 }}>
                                            {idx + 1}.
                                        </Typography>
                                        <TextField
                                            label={`bis ${unit}`}
                                            type="number"
                                            value={step[field] != null ? step[field] : ''}
                                            onChange={(e) => handleOverlayStepChange(ruleKey, idx, field, e.target.value)}
                                            size="small"
                                            sx={{ width: 100 }}
                                        />
                                        <TextField
                                            label="Malus"
                                            type="number"
                                            value={step.penalty != null ? step.penalty : ''}
                                            onChange={(e) => handleOverlayStepChange(ruleKey, idx, 'penalty', e.target.value)}
                                            size="small"
                                            sx={{ width: 80 }}
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* ── Buttons ── */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                    variant="contained" color="primary"
                    onClick={handleSave}
                    disabled={saving || !weightSumOk}
                >
                    {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Speichern
                </Button>
                <Button
                    variant="outlined" color="secondary"
                    onClick={handleSimulate}
                    disabled={simulating}
                >
                    {simulating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Simulieren (Vorschau)
                </Button>
            </Stack>

            {/* ── Simulation Result ── */}
            {simulation && (
                <Card sx={{ backgroundColor: theme.palette.mode === 'dark' ? colors.primary[400] : '#f5f5f5' }}>
                    <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Simulations-Ergebnis
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
                            Schwellen: Grün &ge; {thresholds.green}, Gelb &ge; {thresholds.yellow}
                            {' | '}Score-Verteilung: Min {simulation.score_distribution?.min}, Median {simulation.score_distribution?.median}, Max {simulation.score_distribution?.max}
                        </Typography>

                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <Chip label={`${simulation.green} Grün`} sx={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 'bold' }} />
                            <Chip label={`${simulation.yellow} Gelb`} sx={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 'bold' }} />
                            <Chip label={`${simulation.red} Rot`} sx={{ backgroundColor: '#f44336', color: '#fff', fontWeight: 'bold' }} />
                            <Chip label={`${simulation.total} Gesamt`} variant="outlined" />
                            <Chip label={`${simulation.score_changes || 0} Score-Änderungen`} variant="outlined" />
                        </Stack>

                        {simulation.score_changes > 0 && (
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    {simulation.changes} Signal-Änderungen, {simulation.score_changes} Score-Änderungen gegenüber aktuellem Ergebnis:
                                </Typography>
                                {simulation.changed_positions.map((p, i) => (
                                    <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ ml: 1, mb: 0.3 }}>
                                        <Typography variant="body2" sx={{ minWidth: 100 }}>{p.identifier}</Typography>
                                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: SIGNAL_COLORS[p.original_signal] || '#9e9e9e' }} />
                                        <Typography variant="body2">→</Typography>
                                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: SIGNAL_COLORS[p.simulated_signal] }} />
                                        <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                            ({p.original_portfolio_action_score != null ? Math.round(p.original_portfolio_action_score) : '—'} → {Math.round(p.portfolio_action_score)})
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                        )}

                        {simulation.score_changes === 0 && (
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                Keine Signal- oder Score-Änderungen — aktuelle Verteilung bleibt identisch.
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default AdminTabTrafficLight;
